import React from 'react';
import { ElectionType, electionTypeMap } from '../../api/api-types';
import { usePartyDetailsUrl } from '../../application/PartyDetailsUrlContext';
import { useRouter } from '../../application/Router';
import { ButtonVariant } from '../../styles/buttons';
import { createSearchParams } from '../../utils/search-params';
import { generateRedirectUrl } from '../../utils/url-helpers';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';
import Button from '../Button/Button';
import { rem } from '../../styles/rem';

const fullWidthButtonQuery = '@media (max-width: 39.99em)';

type Props = {
  partyId: string;
  electionType: ElectionType;
  action?: string;
  isOpen?: boolean;
  onDismiss?: () => void;
  cancelRedirectUrl?: string;
  label?: string;
  variant?: ButtonVariant;
  returnUrl?: string;
};

type FormProps = {
  children: React.ReactNode;
  action: string;
  partyId: string;
  electionType: ElectionType;
  service: 'nemid' | 'mitid';
  returnUrl?: string;
  cancelRedirectUrl?: string;
};

const DeclarationForm = ({
  children,
  action,
  electionType,
  partyId,
  service,
  returnUrl,
  cancelRedirectUrl,
}: FormProps) => {
  const { pathname, searchParams } = useRouter();
  const { partyDetailsUrl } = usePartyDetailsUrl();

  // Create the return SearchParams, including any existing params
  const params = createSearchParams(searchParams);
  params.set('election', electionTypeMap[electionType]);
  params.set('party', partyId);
  params.set('action', action);
  const fallbackReturnUrl = `${partyDetailsUrl}?election=${electionTypeMap[electionType]}&party=${partyId}`;
  const explicitReturnUrl = returnUrl ?? fallbackReturnUrl;
  const currentPageReturnUrl = pathname + '?' + params.toString();
  const redirectUrl = explicitReturnUrl || currentPageReturnUrl;

  const actionUrl = generateRedirectUrl(
    `/api/v1/signing/${service}/${action}`,
    redirectUrl,
    cancelRedirectUrl ?? fallbackReturnUrl,
  );

  return (
    <form
      action={actionUrl}
      onSubmit={
        process.env.STORYBOOK
          ? (event) => {
              // We can't use the form action in storybook, so prevent it
              event.preventDefault();
              // eslint-disable-next-line no-console
              console.log('Submit', actionUrl);
            }
          : undefined
      }
      sx={{ textAlign: 'center' }}
      method="POST"
    >
      <input type="hidden" name="electionType" value={electionType} />
      <input type="hidden" name="partyId" value={partyId} />
      {children}
    </form>
  );
};

export const ConfirmDeclaration = ({
  partyId,
  electionType,
  label,
  returnUrl,
  cancelRedirectUrl,
}: Props) => {
  return (
    <DeclarationForm
      action={DeclarationActionTypes.confirm}
      partyId={partyId}
      service="mitid"
      electionType={electionType}
      returnUrl={returnUrl}
      cancelRedirectUrl={cancelRedirectUrl}
    >
      <Button
        variant={ButtonVariant.Primary}
        sx={{ width: 'auto' }}
        type="submit"
      >
        {label}
      </Button>
    </DeclarationForm>
  );
};

export const WithdrawDeclaration = ({
  partyId,
  electionType,
  label,
  variant = ButtonVariant.Primary,
  returnUrl,
  cancelRedirectUrl,
}: Props) => {
  return (
    <DeclarationForm
      action={DeclarationActionTypes.withdraw}
      partyId={partyId}
      electionType={electionType}
      service="mitid"
      returnUrl={returnUrl}
      cancelRedirectUrl={cancelRedirectUrl}
    >
      <Button
        sx={{
          textDecoration: 'none',
          width: 'auto',
          [fullWidthButtonQuery]: {
            width: '100%',
          },
          mr: [rem(16)],
        }}
        variant={variant}
        type="submit"
      >
        {label}
      </Button>
    </DeclarationForm>
  );
};
